<template>
  <a-modal
    :visible="visible"
    title="Registra un nuevo referido"
    @cancel="handleClose"
    centered
    :footer="null"
    class="register-referral-modal"
    :maskClosable="false"
  >
    <CustomScrollbar class="scrollbar" height="calc(100vh - 180px)" auto>
      <div class="modal-form">
        <h2 class="modal-title-text">Registra un nuevo referido</h2>
        <p class="modal-subtext">
          Registra a tu referido y nosotros los contactaremos en un máximo de 48 horas.
          Si este se convierte en un cliente, te notificaremos y recibirás tu giftcard.
        </p>

        <a-form layout="vertical" @submit.prevent="handleSubmit" :rules="rules" ref="formRef" :model="form">
          <a-form-item label="Nombre del referido" required name="first_name">
            <a-input v-model:value="form.first_name" placeholder="Escribe el nombre del referido" />
          </a-form-item>

          <a-form-item label="Apellido del referido" required name="last_name">
            <a-input v-model:value="form.last_name" placeholder="Escribe el apellido del referido" />
          </a-form-item>

          <a-form-item label="Correo del referido" required name="email">
            <a-input
              v-model:value="form.email"
              placeholder="Escribe el correo del referido"
            />
          </a-form-item>

          <a-form-item label="Teléfono del referido" required name="phone_number">
            <a-row :gutter="24">
              <a-col span="7">
                <selector-country
                  v-model:value="code_phone"
                  @codePhone="form.code_country = $event"
                />
              </a-col>
              <a-col span="17">
                <a-input
                  v-model:value="form.phone_number"
                  placeholder="Número de teléfono"
                  @keypress="handleNumberPhone($event)"
                />
              </a-col>
            </a-row>
          </a-form-item>

          <a-form-item label="Cargo del referido" required name="charge">
            <a-input v-model:value="form.charge" placeholder="Escribe el cargo o puesto del referido" />
          </a-form-item>

          <a-form-item label="Nombre de la empresa del referido" required name="company">
            <a-input v-model:value="form.company" placeholder="Escribe el nombre de la empresa del referido" />
          </a-form-item>

          <a-form-item label="Página web de la empresa del referido" required name="company_website">
            <a-input v-model:value="form.company_website" placeholder="Escribe la página web del referido" />
          </a-form-item>

          <a-form-item label="Comentario adicional o contexto" required name="comment">
            <a-textarea
              v-model:value="form.comment"
              :maxlength="1000"
              placeholder="Escribir aquí..."
              :auto-size="{ minRows: 3, maxRows: 6 }"
            />
            <div class="char-count">{{ form.comment.length }}/1000</div>
          </a-form-item>
        </a-form>
      </div>
    </CustomScrollbar>

    <div class="modal-actions">
      <a-button @click="handleClose">Cancelar</a-button>

      <a-tooltip>
        <template #title v-if="!isFormValid">
          Completa los campos requeridos para poder continuar.
        </template>
        <a-button type="primary" @click="handleSubmit" :disabled="!isFormValid">Registrar referido</a-button>
      </a-tooltip>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, createVNode, ref, watch } from "vue";
import SelectorCountry from "@/app/shared/components/selectors/SelectorCountry.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { message, Modal } from "ant-design-vue";
import { referralService } from "../../service/ReferralService";
import { ReferredUser } from "../../utils/IReferral";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";
import { ResponseError } from '@/app/shared/utils/httpManager';
defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
});
const emit = defineEmits(['update','update:visible']);

const form = ref<ReferredUser>({
  first_name: "",
  last_name: "",
  email: "",
  code_country: '+51',
  phone_number: "",
  charge: "",
  company: "",
  company_website: "",
  comment: "",
});

const formRef = ref();
const code_phone = ref(1);
const isFormValid = ref(false);

const isFormComplete = computed(() => {
  const f = form.value;
  return (
    !!f.first_name.trim() &&
    !!f.last_name.trim() &&
    !!f.email.trim() &&
    !!String(f.code_country).trim() &&
    !!f.phone_number.trim() &&
    !!f.charge.trim() &&
    !!f.company.trim() &&
    !!f.company_website.trim() &&
    !!f.comment.trim()
  );
});

const isFormDirty = computed(() => {
  const f = form.value;
  return (
    !!f.first_name.trim() ||
    !!f.last_name.trim() ||
    !!f.email.trim() ||
    !!f.phone_number.trim() ||
    !!f.charge.trim() ||
    !!f.company.trim() ||
    !!f.company_website.trim() ||
    !!f.comment.trim()
  );
});

watch(
  form,
  async () => {
    if (!isFormComplete.value) { isFormValid.value = false; return; }
    if (!formRef.value) return;
    try {
      await formRef.value.validateFields();
      isFormValid.value = true;
    } catch {
      isFormValid.value = false;
    }
  },
  { deep: true, immediate: true, flush: 'post' }
);

const NAME_COMPANY_REGEX = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9 .]+$/; // letras (con acentos), números, espacio, . ' -
// Letras (incluye acentos y Ñ/ñ), espacios y punto. Sin números.
const NAME_REGEX = /^(?=.*[A-Za-zÁÉÍÓÚÜÑáéíóúüñ])[A-Za-zÁÉÍÓÚÜÑáéíóúüñ .]+$/;

const EMAIL_SAFE_REGEX   = /^[A-Za-z0-9@._+\-]+$/;             // ASCII típico en emails
const URL_SAFE_REGEX     = /^[A-Za-z0-9\-._~:/?#\[\]@!$&'()*+,;=%]+$/; // RFC3986 básico

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const rules = {
  first_name: [
    { required: true, message: "Campo requerido", whitespace: true },
    {
      validator: (_: any, value: string) => {
        const v = (value || "").trim();
        if (!v) return Promise.reject("Campo requerido");
        if (!NAME_REGEX.test(v)) return Promise.reject("Nombre del referido inválido");
        return Promise.resolve();
      },
      trigger: "blur",
    },
  ],
  last_name: [
    { required: true, message: "Campo requerido", whitespace: true },
    {
      validator: (_: any, value: string) => {
        const v = (value || "").trim();
        if (!v) return Promise.reject("Campo requerido");
        if (!NAME_REGEX.test(v)) return Promise.reject("Apellido del referido inválido");
        return Promise.resolve();
      },
      trigger: "blur",
    },
  ],
  email: [
    { required: true, message: "Campo requerido", whitespace: true },
    {
      validator: (_: any, value: string) => {
        const v = (value || "").trim();
        if (!v) return Promise.reject("Campo requerido");
        if (/^https?:\/\//i.test(v)) return Promise.reject("Correo del referido inválido");
        if (!EMAIL_SAFE_REGEX.test(v)) return Promise.reject("Correo del referido inválido");
        if (!emailRegex.test(v)) return Promise.reject("Correo del referido inválido");
        return Promise.resolve();
      },
      trigger: "blur",
    },
  ],
  code_country: [{ required: true, message: "Campo requerido" }],
  phone_number: [
    { required: true, message: "Campo requerido", whitespace: true },
    { pattern: /^([0-9])*$/g, message: "Solo números" },
  ],
  charge: [
    { required: true, message: "Campo requerido", whitespace: true },
    {
      validator: (_: any, value: string) => {
        const v = (value || "").trim();
        if (!v) return Promise.reject("Campo requerido");
        if (!NAME_COMPANY_REGEX.test(v)) return Promise.reject("Cargo del referido inválido");
        return Promise.resolve();
      },
      trigger: "blur",
    },
  ],
  company: [
    { required: true, message: "Campo requerido", whitespace: true },
    {
      validator: (_: any, value: string) => {
        const v = (value || "").trim();
        if (!v) return Promise.reject("Campo requerido");
        if (!NAME_COMPANY_REGEX.test(v)) return Promise.reject("Nombre de la empresa inválido");
        return Promise.resolve();
      },
      trigger: "blur",
    },
  ],
  company_website: [
    { required: true, message: "Campo requerido", whitespace: true },
    {
      validator: (_: any, value: string) => {
        const v = (value || "").trim();
        if (!v) return Promise.reject("Campo requerido");
        if (!/^https?:\/\//i.test(v)) return Promise.reject("La página web debe iniciar con http:// o https://");
        try {
          new URL(v);
        } catch {
          return Promise.reject("Página web inválida");
        }
        if (!URL_SAFE_REGEX.test(v)) return Promise.reject("Página web inválida");
        return Promise.resolve();
      },
      trigger: "blur",
    },
  ],
  comment: [
    { required: true, message: "Campo requerido", whitespace: true },
  ],
};

const handleSubmit = async () => {
  
    Modal.confirm({
      title: `¿Estás seguro de registrar este referido?`,
      content: "Verifica que la información ingresada sea correcta antes de continuar con el registro.",
      icon: createVNode(QuestionCircleOutlined),
      onOk: async () => {
        try{
          const body = { ...form.value, code_country: `+${form.value?.code_country}`}
          await referralService.createReferral(body);
          message.success("Referido registrado con éxito.");
          emit('update');
          close();
        }catch (error: any) {
          const messageError = error instanceof ResponseError ? error.getCleanErrorMessage() : "Ha ocurrido un error";
          console.error("Error registering referred user:", messageError);
          if (messageError.includes("correo")){
            message.error(messageError);
          } else {
            message.error("Completos los datos correctamente para poder continuar");
          };
          
        }
      },
      centered: true,
      okText: "Sí, registrar",
      cancelText: "No, cancelar",
    });
    
  console.log("Datos enviados:", form.value);
  // aquí conectas con tu API o servicio
  
};

const handleClose = () => {
  if (isFormDirty.value){
    Modal.confirm({
      title: `¿Estás seguro de descartar cambios?`,
      content: "Al cerrar no se guardarán los datos previamente ingresados.",
      icon: createVNode(QuestionCircleOutlined),
      centered: true,
      onOk: () => close(),
      okText: "Sí, descartar cambios",
      cancelText: "No, seguir editando",
    });
  } else {
    close();
  }
};

const close = () => {
  emit('update:visible', false)
};

const handleNumberPhone = (e: { keyCode: number; preventDefault: () => void }) => {
  const char = String.fromCharCode(e.keyCode);
  if (/^[0-9]+$/.test(char)) return true;
  else e.preventDefault();
};
</script>

<style lang="sass" scoped>
.register-referral-modal 
  .ant-modal-body 
    padding-top: 0.5rem
  
  .modal-subtext 
    font-size: 0.9rem
    color: #666
    margin-bottom: 1rem

  .phone_number-input 
    display: flex
    gap: 0.5rem

    .ant-input 
      flex: 1

  .char-count 
    font-size: 0.75rem
    text-align: right
    color: #888
    margin-top: 0.25rem
  
  .modal-actions 
    display: flex
    justify-content: center
    gap: 0.8rem
    margin-top: 1rem

.modal-title-text
  display: flex
  justify-content: center

.modal-form
  margin: 0px 21px 0 21px
</style>
